import React, { useRef, useState, useEffect, forwardRef } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../index.css'
import { shootFullScreen } from './Animations/Confetti'
import { io } from 'socket.io-client'

const socket = io(['https://apolop2p-landing-backend.vercel.app/'], {
  withCredentials: true,
})

const LandingPageModal = ({ onClose, type }) => {
  const modalRef = useRef(null)
  const [isClosing, setIsClosing] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [dob, setDob] = useState(null)
  const [countries, setCountries] = useState([])

  const CustomInput = forwardRef(({ value, onClick, onChange, placeholder }, ref) => (
    <input
      ref={ref}
      onClick={onClick}
      onChange={onChange}
      value={value}
      placeholder={placeholder}
      className="w-full rounded-md border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
    />
  ))

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    role: type || '',
  })

  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    country: '',
    submitError: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const now = new Date()
  const localYear = now.getFullYear() - 18
  const localMonth = now.getMonth()
  const localDay = now.getDate()
  const eighteenYearsAgo = new Date(localYear, localMonth, localDay, 0, 0, 0, 0)

  const years = []
  for (let yr = 1940; yr <= localYear; yr++) {
    years.push(yr)
  }

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://restcountries.com/v3.1/all?fields=name')
        if (!response.ok) throw new Error('Failed to fetch countries')

        const data = await response.json()
        const sortedCountries = data
          .map((country) => country.name.common)
          .sort((a, b) => a.localeCompare(b))

        setCountries(sortedCountries)
      } catch (error) {
        console.error('Error fetching countries:', error)
      }
    }

    fetchCountries()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 10)
    return () => clearTimeout(timer)
  }, [])

  const isOldEnough = (selectedDate) => {
    if (!selectedDate) return false
    const today = new Date()
    const birthDatePlus18 = new Date(
      selectedDate.getFullYear() + 18,
      selectedDate.getMonth(),
      selectedDate.getDate()
    )
    return today >= birthDatePlus18
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let formattedValue = value
    if ((name === 'firstName' || name === 'lastName') && value.length > 0) {
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1)
    }
    setFormData((prev) => ({ ...prev, [name]: formattedValue }))
    setFieldErrors((prev) => ({ ...prev, [name]: '', submitError: '' }))
  }

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
    }, 500)
  }

  const handleButtonClick = async (role) => {
    if (!modalRef.current) return
    setIsSubmitting(true)
    setFieldErrors({
      firstName: '',
      lastName: '',
      dob: '',
      email: '',
      country: '',
      submitError: '',
    })

    const updatedFormData = { ...formData, role }
    const { firstName, lastName, email, country } = updatedFormData

    let hasError = false
    const newFieldErrors = {
      firstName: '',
      lastName: '',
      dob: '',
      email: '',
      country: '',
      submitError: '',
    }

    if (!firstName) {
      hasError = true
      newFieldErrors.firstName = 'First name is required.'
    } else {
      const nameRegex = /^[A-Z][a-zA-Z]+$/
      if (!nameRegex.test(firstName)) {
        hasError = true
        newFieldErrors.firstName = 'Must start with an uppercase letter and contain only letters.'
      }
    }

    if (!lastName) {
      hasError = true
      newFieldErrors.lastName = 'Surname is required.'
    } else {
      const nameRegex = /^[A-Z][a-zA-Z]+$/
      if (!nameRegex.test(lastName)) {
        hasError = true
        newFieldErrors.lastName = 'Must start with an uppercase letter and contain only letters.'
      }
    }

    if (!dob) {
      hasError = true
      newFieldErrors.dob = 'Date of birth is required.'
    } else if (!isOldEnough(dob)) {
      hasError = true
      newFieldErrors.dob = 'You must be at least 18 years old to sign up.'
    }

    if (!email) {
      hasError = true
      newFieldErrors.email = 'Email is required.'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i
      if (!emailRegex.test(email)) {
        hasError = true
        newFieldErrors.email = 'You must enter a proper email format'
      }
    }

    if (!country) {
      hasError = true
      newFieldErrors.country = 'Please select your country of residence.'
    }

    if (hasError) {
      setFieldErrors(newFieldErrors)
      setIsSubmitting(false)
      return
    }

    try {
      const dobString = dob.toISOString().split('T')[0]
      const username = process.env.REACT_APP_EMAIL_USER
      const password = process.env.REACT_APP_EMAIL_PASSWORD
      const slimData = { dateOfBirth: dobString, firstName, lastName, email, country, role }

      shootFullScreen()
      handleClose()
      const base64Credentials = btoa(`${username}:${password}`)

      const response = await fetch('https://apolop2p-landing-backend.vercel.app/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${base64Credentials}`,
        },
        body: JSON.stringify(slimData),
        keepalive: false,
      })

      if (!response.ok) {
        const result = await response.json()
        throw new Error(result.error || 'Registration failed')
      }

      await response.json()
      socket.emit('sendEmail', { firstName, email, role })
    } catch (err) {
      setFieldErrors((prev) => ({
        ...prev,
        submitError: err.message || 'Something went wrong.',
      }))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      id="modal"
      ref={modalRef}
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${isClosing ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500`}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-lg p-6 md:p-8 overflow-y-auto max-h-[90vh]">
        <button
          onClick={handleClose}
          className="absolute top-3 right-4 text-gray-600 hover:text-black text-2xl"
        >
          &times;
        </button>

        <h2 className="text-xl text-[#f78430] md:text-2xl font-semibold text-center mb-6">
          Presubscribe for Early Access
        </h2>

        <form className="space-y-4">
          <input
            id="firstName"
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          {fieldErrors.firstName && <p className="text-red-500 text-sm">{fieldErrors.firstName}</p>}

          <input
            id="lastName"
            type="text"
            name="lastName"
            placeholder="Surname"
            value={formData.lastName}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          {fieldErrors.lastName && <p className="text-red-500 text-sm">{fieldErrors.lastName}</p>}

          <DatePicker
            selected={dob}
            onChange={(date) => {
              setDob(date)
              setFieldErrors((prev) => ({ ...prev, dob: '', submitError: '' }))
            }}
            minDate={new Date(1940, 0, 1)}
            maxDate={eighteenYearsAgo}
            placeholderText="Date of Birth"
            dateFormat="MM/dd/yyyy"
            customInput={<CustomInput />}
          />
          {fieldErrors.dob && <p className="text-red-500 text-sm">{fieldErrors.dob}</p>}

          <input
            id="email"
            type="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          {fieldErrors.email && <p className="text-red-500 text-sm">{fieldErrors.email}</p>}

          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 p-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:outline-none"
          >
            <option value="">Select Country of Residence</option>
            {countries.length > 0 ? (
              countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))
            ) : (
              <option disabled>Loading countries...</option>
            )}
          </select>
          {fieldErrors.country && <p className="text-red-500 text-sm">{fieldErrors.country}</p>}

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => handleButtonClick('Student')}
              className="flex-1 bg-[#f78430] hover:bg-orange-600 text-white rounded-md py-3 text-sm font-medium disabled:opacity-50"
            >
              Sign up as Student
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => handleButtonClick('Tutor')}
              className="flex-1 bg-[#f78430] hover:bg-orange-600 text-white rounded-md py-3 text-sm font-medium disabled:opacity-50"
            >
              Sign up as Tutor
            </button>
          </div>

          {fieldErrors.submitError && (
            <p className="text-red-500 text-center text-sm">{fieldErrors.submitError}</p>
          )}
        </form>
      </div>
    </div>
  )
}

export default LandingPageModal
